import type { BillingNote } from '@policyvision/contracts';

import { Client } from '@/common/services/Client';
import Paginated from '@/common/types/paginated';

import { BillingSortField, BillingStatus, SortOrder } from '../types/SortTypes';

export interface BillingSummary {
  uuid: string;
  searchCode: string | null;
  accountName: string;
  policyNumber: string;
  policyType: string | null;
  insurer: string;
  activityCode: string | null;
  effectiveDate: string;
  expiryDate: string;
  status: BillingStatus;
  cv1: string | null;
  gc1: string | null;
  technicalSupport: string | null;
  isAgencyBill: boolean;
  inProgressForUser: string | null;
  service: string | null;
  entity: string | null;
  completedDate: string | null;
  assignedUserId: string | null;
  assignedUserName: string | null;
  latestNote: Pick<BillingNote, 'templateId' | 'content'> | null;
  createdAt: string;
  attachmentIds: number[];
  policyGuid: string;
  entityGuid: string;
}

export type PaginationParams = {
  pageIndex: number;
  pageSize: number;
  sortBy: BillingSortField;
  sortOrder: SortOrder;
};

export type BillingFilters = {
  searchCode: string;
  accountName: string;
  policyNumber: string;
  policyType: string;
  insurer: string;
  activityCode: string;
  isAgencyBill: string;
  assignedUserId: string;
  technicalSupport: string;
  statuses: string[];
  entity: string;
  service: string;
  gc1: string;
};

export interface PaginatedBillingsResponse {
  billings: BillingSummary[];
  totalPages: number;
  totalBillings: number;
}

const defaultParams: PaginationParams = {
  pageIndex: 0,
  pageSize: 10,
  sortBy: 'createdAt',
  sortOrder: 'desc',
};

export async function fetchPaginatedBillingsSummary(
  params = defaultParams,
  filters?: Partial<BillingFilters>,
  signal?: AbortSignal,
): Promise<Paginated<BillingSummary>> {
  const queryParams: Record<string, string | number> = {
    pageIndex: params.pageIndex,
    pageSize: params.pageSize,
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  };

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        queryParams[key] = value.join(',');
      } else {
        queryParams[key] = value;
      }
    });
  }

  const response = await Client.get<PaginatedBillingsResponse>({
    endpoint: `/billing/summary/paginated`,
    params: queryParams,
    signal,
  });

  return {
    items: response.data.billings,
    totalItems: response.data.totalBillings,
    totalPages: response.data.totalPages,
  };
}
